import React from 'react'
import { useAppDispatch } from '../../../store/hooks'
import { openModal } from '../../../store/modal/modal'
import { type ToolUse } from '../../../models/StreamingAIAgent'
import { AIAgentType } from '../../../models/AIAgent'
import { type User } from '../../../models/User'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../common/integrations'
import { useIntegrations } from '../../Integration/useIntegrations'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { getUserActionPayload } from '../../AIAgent/UserActions/model'
import { IntegrationUserAction } from '../../AIAgent/UserActions/IntegrationUserAction'
import { IntegrationUserActionButton } from '../../AIAgent/UserActions/IntegrationUserActionButton'
import { useTrackUserActionButtonClick } from '../../AIAgent/UserActions/useTrackUserActionButtonClick'

interface DocumentSummaryChatToolUseProps {
  toolUse: ToolUse
  contact: User
}

export const DocumentSummaryChatToolUse: React.FC<DocumentSummaryChatToolUseProps> = ({ toolUse, contact }) => {
  switch (toolUse.name) {
    case 'offer_user_action':
      return <DocumentSummaryChatUserAction toolUse={toolUse} contact={contact} />
    default:
      return null
  }
}

const DocumentSummaryChatUserAction: React.FC<DocumentSummaryChatToolUseProps> = ({ toolUse, contact }) => {
  const userAction = getUserActionPayload(toolUse)

  switch (userAction?.type) {
    case 'save_document_summary':
      return <DocumentSummaryChatSaveSummaryUserAction serviceId={userAction.integration_service_id} />
    case 'save_summary_as_pdf_document':
      return <DocumentSummaryChatSaveSummaryAsPdfUserAction serviceId={userAction.integration_service_id} />
    case 'save_contact_details':
      return (
        <DocumentSummaryChatSaveContactDetailsUserAction serviceId={userAction.integration_service_id} contactId={userAction.contact_id} contact={contact} />
      )
    default:
      return null
  }
}

interface DocumentSummaryChatIntegrationUserActionProps {
  serviceId: IntegrationServiceId
}

const DocumentSummaryChatSaveSummaryUserAction: React.FC<DocumentSummaryChatIntegrationUserActionProps> = ({ serviceId }) => {
  const dispatch = useAppDispatch()
  const { supportsDocumentSummaryExport } = useIntegrations()

  const trackButtonClick = useTrackUserActionButtonClick({ agentType: AIAgentType.DOCUMENT_SUMMARY, actionType: 'save_document_summary', serviceId })

  const handleSaveSummaryClick = () => {
    trackButtonClick('save')
    dispatch(openModal({ type: 'exportDocumentSummary', serviceId }))
  }

  return (
    <IntegrationUserAction serviceId={serviceId} isSupported={supportsDocumentSummaryExport(serviceId)} onConnectionClick={trackButtonClick}>
      <IntegrationUserActionButton serviceId={serviceId} label={`Save summary to ${getIntegrationDisplayName(serviceId)}`} onClick={handleSaveSummaryClick} />
    </IntegrationUserAction>
  )
}

const DocumentSummaryChatSaveSummaryAsPdfUserAction: React.FC<DocumentSummaryChatIntegrationUserActionProps> = ({ serviceId }) => {
  const dispatch = useAppDispatch()
  const { supportsDocumentUpload } = useIntegrations()

  const trackButtonClick = useTrackUserActionButtonClick({
    agentType: AIAgentType.DOCUMENT_SUMMARY,
    actionType: 'save_summary_as_pdf_document',
    serviceId,
  })

  const handleSaveSummaryAsPdfClick = () => {
    trackButtonClick('save')
    dispatch(openModal({ type: 'saveDocumentSummaryToDocumentConnector', serviceId }))
  }

  return (
    <IntegrationUserAction serviceId={serviceId} isSupported={supportsDocumentUpload(serviceId)} onConnectionClick={trackButtonClick}>
      <IntegrationUserActionButton
        serviceId={serviceId}
        label={`Save summary as PDF to ${getIntegrationDisplayName(serviceId)}`}
        onClick={handleSaveSummaryAsPdfClick}
      />
    </IntegrationUserAction>
  )
}

interface DocumentSummaryChatSaveContactDetailsUserActionProps extends DocumentSummaryChatIntegrationUserActionProps {
  contactId: string
  contact: User
}

const DocumentSummaryChatSaveContactDetailsUserAction: React.FC<DocumentSummaryChatSaveContactDetailsUserActionProps> = ({ serviceId, contactId, contact }) => {
  const dispatch = useAppDispatch()

  const { canCreateOrUpdateContact } = useIntegrations()

  const trackButtonClick = useTrackUserActionButtonClick({ agentType: AIAgentType.DOCUMENT_SUMMARY, actionType: 'save_contact_details', serviceId })

  const isSupported = contact.id === contactId && canCreateOrUpdateContact(serviceId, contact)

  const handleSaveContactDetailsClick = () => {
    trackButtonClick('save')
    dispatch(openModal({ type: 'updateDocumentContact', serviceId, user: contact }))
  }

  return (
    <IntegrationUserAction serviceId={serviceId} isSupported={isSupported} onConnectionClick={trackButtonClick}>
      <IntegrationUserActionButton
        serviceId={serviceId}
        label={`Save contact details to ${getIntegrationDisplayName(serviceId)} (${getUserDisplayName(contact)})`}
        onClick={handleSaveContactDetailsClick}
      />
    </IntegrationUserAction>
  )
}
